import { BaseSelectRef } from 'rc-select';
import { createAsyncSelectors } from '@lib/selectors';
import { getClosestPopup } from '@lib/popups';
import { getInitiativeJiraIssuesSelector } from '../../@arch/selectors';
import { integrationsActions } from '@sonar-software/sonar-integrations';
import { JiraIssue } from './JiraIssue';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { PLACEHOLDERS_COPY } from '@sonar-software/sonar-ts-constants';
import { Select } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAction, useCurrentUser, useJiraStatuses, useSonarSelector } from '@hooks';

const getJiraIssuesSelectors = createAsyncSelectors((state) => state.integrations.jira.issues.get);

export interface JiraIssuesPopoverProps {
	initiativeId: number;
}

export const JiraIssuesPopover: React.FC<JiraIssuesPopoverProps> = ({ initiativeId }) => {
	const [ query, setQuery ] = useState<string | undefined>();
	useJiraStatuses();
	const getJiraIssues = useAction(integrationsActions.requestGetJiraIssues);
	const putJiraInitiativesLink = useAction(integrationsActions.requestPutJiraInitiativesLinkIssue);
	const resetJiraIssues = useAction(integrationsActions.resetGetJiraIssues);
	const jiraIssuesData = useSonarSelector(getJiraIssuesSelectors.data);
	const initiativeJiraIssues = useSonarSelector(getInitiativeJiraIssuesSelector.data);
	const jiraIssuesRequested = useSonarSelector(getJiraIssuesSelectors.isRequested);
	const selectRef = useRef<BaseSelectRef>(null);
	const { customerId } = useCurrentUser();

	useEffect(() => {
		selectRef.current?.focus();
	}, [ selectRef ]);

	const handleOnClear = useCallback(() => {
		setQuery(undefined);

		resetJiraIssues();
	}, [ resetJiraIssues ]);

	const handleOnSearch = useCallback(
		(value: string) => {
			setQuery(value || undefined);

			if (value?.length >= 3) {
				getJiraIssues({ searchText: value });
			} else {
				resetJiraIssues();
			}
		},
		[ getJiraIssues, resetJiraIssues ]
	);

	const options = useMemo(() => {
		const jiraIssueOptions: Array<{ label: JSX.Element; value: string }> = [];
		jiraIssuesData?.forEach((issue) => {
			const { issueKey, issueName, statusId, projectKey } = issue;

			if (!initiativeJiraIssues?.some((jiraIssue) => jiraIssue.issueKey === issueKey)) {
				jiraIssueOptions.push({
					label:
	<JiraIssue
		initiativeId={ initiativeId }
		issueKey={ issueKey }
		issueName={ issueName }
		projectKey={ projectKey }
		statusId={ statusId } />,
					value: issueKey
				});
			}
		});

		return jiraIssueOptions;
	}, [ initiativeId, initiativeJiraIssues, jiraIssuesData ]);

	const handleOnSelect = (selected: string) => {
		const jiraIssue = jiraIssuesData?.filter((jiraStatus) => jiraStatus.issueKey === selected);

		if (jiraIssue) {
			const [ { issueKey, issueName, statusId, projectKey } ] = jiraIssue;

			putJiraInitiativesLink({
				customerId: customerId,
				initiativeId: initiativeId,
				issueKey: issueKey,
				issueName: issueName,
				projectKey: projectKey,
				statusId: statusId
			});
		}
	};

	return (
		<div id='ant-popover'>
			<Select
				allowClear
				className='jira-issue-select'
				filterOption={ false }
				getPopupContainer={ getClosestPopup }
				notFoundContent={ jiraIssuesRequested ? <LoadingSpinner /> : null }
				onClear={ handleOnClear }
				onSearch={ handleOnSearch }
				onSelect={ handleOnSelect }
				options={ options }
				placeholder={ PLACEHOLDERS_COPY.SEARCH_JIRA_ISSUES }
				ref={ selectRef }
				showSearch
				value={ query } />
		</div>
	);
};
